/**
 * Secure Provider Login Vault UI
 * 
 * Settings > Connections feature for managing saved logins for:
 * - Credit Monitoring Services (IdentityIQ, SmartCredit, MyScoreIQ)
 * - Bureau Logins (Experian, TransUnion, Equifax)
 * 
 * Security Rules:
 * - Never preload raw passwords or identity values into edit forms
 * - Show "Password saved" / "Verification value saved" indicators
 * - Block all sensitive operations in support/impersonation mode
 * - No reveal functionality (credentials are for backend use only)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { buildRefreshActionState } from '../../utils/providerRefreshAction';
import { executeProviderRefreshAction } from '../../hooks/useProviderRefreshImport';
import {
  Shield,
  Eye,
  EyeOff,
  Save,
  Pencil,
  Trash2,
  Play,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  AlertTriangle,
  Link as LinkIcon,
  Plug,
  PlugZap,
  Lock,
  Download,
} from 'lucide-react';
import { 
  renderProviderLogo, 
  getProvider, 
  normalizeProviderKey 
} from '../../config/providerRegistry';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// FundElite design system colors
const colors = {
  brandGreen500: '#0FAC6C',
  brandGreen600: '#0D9F64',
  brandGreen700: '#0B8C58',
  mint50: '#F2FCF7',
  mint100: '#E5F8EE',
  mint200: '#D1FAE5',
  softSectionBg: '#F3F6F4',
  borderLight: '#E5E9E7',
  borderDefault: '#D9DEDC',
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textMuted: '#6B7280',
  warning: '#EAB23A',
  error: '#EF4444',
};

// Status badge configurations
const STATUS_CONFIG = {
  not_connected: { label: 'Not Connected', color: 'stone', bgColor: '#F3F4F6', textColor: '#6B7280' },
  saved: { label: 'Saved', color: 'emerald', bgColor: '#D1FAE5', textColor: '#065F46' },
  ready_for_import: { label: 'Ready', color: 'emerald', bgColor: '#D1FAE5', textColor: '#065F46' },
  needs_reauth: { label: 'Needs Re-auth', color: 'amber', bgColor: '#FEF3C7', textColor: '#B45309' },
  invalid_credentials: { label: 'Invalid', color: 'red', bgColor: '#FEE2E2', textColor: '#B91C1C' },
};

// Provider group headers
const GROUP_HEADERS = {
  monitoring_service: {
    title: 'Credit Monitoring Services',
    description: 'Save logins for 3-bureau monitoring services',
    icon: '🔐',
  },
  bureau: {
    title: 'Bureau Logins',
    description: 'Direct access to bureau account logins for secure imports',
    icon: '🏛️',
  },
};

/**
 * Connection Card Component
 */
const ConnectionCard = ({ 
  provider, 
  isSupportMode, 
  onConnect, 
  onEdit, 
  onDelete, 
  onQuickImport,
  onTest,
  isLoading 
}) => {
  const isConnected = provider.is_connected;
  const statusConfig = STATUS_CONFIG[provider.connection_status] || STATUS_CONFIG.not_connected;
  // Check if manual save is supported (new field) - true for all 6 providers now
  const canManualSave = provider.supports_manual_save !== false;
  // Only show "Coming Soon" if manual save is explicitly disabled
  const isPlaceholder = !canManualSave;
  
  return (
    <div
      data-testid={`connection-card-${provider.provider_key}`}
      className={`p-4 rounded-xl border transition-all duration-200 ${
        isConnected 
          ? 'bg-gradient-to-br from-emerald-50 to-white border-emerald-200'
          : 'bg-white border-stone-200 hover:border-stone-300'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {/* Provider Logo - Horizontal rectangular container for wordmark logos */}
          <div 
            className={`w-36 h-12 rounded-xl flex items-center justify-start px-2 overflow-hidden ${
              isConnected ? 'bg-emerald-50' : 'bg-white'
            }`}
          >
            {/* Check if this is a credit monitoring provider with centralized logo */}
            {getProvider(provider.provider_key) ? (
              renderProviderLogo(provider.provider_key, 'md')
            ) : provider.logo_url ? (
              <img 
                src={provider.logo_url} 
                alt={provider.logo_alt || `${provider.display_name} logo`}
                className="w-full h-full object-contain p-1.5"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : (
              <span className="text-lg flex items-center justify-center w-full h-full">
                {provider.icon_emoji}
              </span>
            )}
          </div>
          <div>
            <p className="font-semibold text-stone-900">{provider.display_name}</p>
            <p className="text-xs text-stone-500">{provider.description}</p>
          </div>
        </div>
        
        {/* Status Badge */}
        <div className="flex items-center gap-2">
          {isPlaceholder && !isConnected && (
            <span className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-500">
              Coming Soon
            </span>
          )}
          {isConnected && (
            <span 
              className="text-xs px-2.5 py-1 rounded-full flex items-center gap-1 font-medium"
              style={{ background: statusConfig.bgColor, color: statusConfig.textColor }}
            >
              {statusConfig.label === 'Saved' && <CheckCircle size={12} />}
              {statusConfig.label === 'Needs Re-auth' && <AlertTriangle size={12} />}
              {statusConfig.label === 'Invalid' && <AlertCircle size={12} />}
              {statusConfig.label}
            </span>
          )}
        </div>
      </div>
      
      {/* Connection Details (when connected) */}
      {isConnected && !isSupportMode && (
        <div className="mb-4 space-y-2">
          {/* Username */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-stone-500">Username:</span>
            <span className="font-mono text-stone-700">{provider.username_masked || '***'}</span>
          </div>
          
          {/* Password indicator */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-stone-500">Password:</span>
            <span className="flex items-center gap-1.5 text-emerald-600">
              <Lock size={12} />
              <span className="text-xs font-medium">Saved securely</span>
            </span>
          </div>
          
          {/* Identity value indicator */}
          {provider.has_identity_value && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-stone-500">
                {provider.identity_field_label?.replace(' (optional)', '') || 'Security Answer'}:
              </span>
              <span className="flex items-center gap-1.5 text-emerald-600">
                <Lock size={12} />
                <span className="text-xs font-medium">Saved</span>
              </span>
            </div>
          )}
          
          {/* Last updated */}
          {provider.updated_at && (
            <div className="flex items-center justify-between text-xs text-stone-400 pt-1 border-t border-stone-100">
              <span>Last updated:</span>
              <span>{new Date(provider.updated_at).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Support Mode Warning */}
      {isConnected && isSupportMode && (
        <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-xs text-amber-700 flex items-center gap-2">
            <Shield size={14} />
            Credential details hidden in support mode
          </p>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {isConnected ? (
          <>
            {!isSupportMode && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(provider)}
                  data-testid={`edit-btn-${provider.provider_key}`}
                  disabled={isLoading}
                >
                  <Pencil size={14} className="mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDelete(provider)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-btn-${provider.provider_key}`}
                  disabled={isLoading}
                >
                  <Trash2 size={14} className="mr-1" />
                  Delete
                </Button>
              </>
            )}
            
            {/* Quick Import (only for supported providers, not in support mode) */}
            {provider.supports_quick_import && !isSupportMode && (
              <Button
                size="sm"
                onClick={() => onQuickImport(provider)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`import-btn-${provider.provider_key}`}
                disabled={isLoading}
              >
                <Download size={14} className="mr-1" />
                Quick Import
              </Button>
            )}
          </>
        ) : (
          <Button
            variant={isPlaceholder ? 'outline' : 'default'}
            size="sm"
            disabled={isPlaceholder || isSupportMode || isLoading}
            onClick={() => onConnect(provider)}
            data-testid={`connect-btn-${provider.provider_key}`}
            className={!isPlaceholder && !isSupportMode ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
          >
            {isPlaceholder ? (
              <>
                <Clock size={14} className="mr-1" />
                Coming Soon
              </>
            ) : isSupportMode ? (
              <>
                <Lock size={14} className="mr-1" />
                Blocked
              </>
            ) : (
              <>
                <Plug size={14} className="mr-1" />
                Add Login
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * Connection Modal Component
 * 
 * IMPORTANT: Never preloads raw passwords or identity values.
 * Shows "Password saved" / "Verification value saved" indicators instead.
 */
const ConnectionModal = ({ 
  isOpen, 
  onClose, 
  provider, 
  isEditing,
  onSave,
  isSaving 
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [identityValue, setIdentityValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      // NEVER preload raw password or identity value
      setUsername('');
      setPassword('');
      setIdentityValue('');
      setShowPassword(false);
    }
  }, [isOpen, provider]);
  
  const handleSubmit = () => {
    // Validation - require all fields including identity value
    if (!isEditing && (!username.trim() || !password.trim() || !identityValue.trim())) {
      toast.error('All fields are required including Last 4 SSN or Security Answer');
      return;
    }
    
    if (isEditing && !username.trim() && !password.trim() && !identityValue.trim()) {
      toast.error('Please enter at least one field to update');
      return;
    }
    
    onSave({
      username_or_email: username.trim() || null,
      password: password.trim() || null,
      identity_value: identityValue.trim() || null,
    });
  };
  
  if (!provider) return null;
  
  // Generate provider-specific modal title
  const getModalTitle = () => {
    if (isEditing) {
      return `Update ${provider.display_name} Login`;
    }
    // Use "Connect to" for bureaus to be clearer
    if (provider.provider_group === 'bureau') {
      return `Connect to ${provider.display_name}`;
    }
    return `Connect ${provider.display_name}`;
  };
  
  const getModalDescription = () => {
    if (isEditing) {
      return 'Update your saved credentials. Leave fields blank to keep existing values.';
    }
    if (provider.provider_group === 'bureau') {
      return 'Enter your bureau login credentials to save securely for future imports.';
    }
    return 'Enter your login credentials to save for quick imports.';
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="connection-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {/* Provider Logo in Modal */}
            <div 
              className={`w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden shrink-0 ${
                provider.logo_bg === 'dark' ? 'bg-stone-900' : 'bg-white border border-stone-200'
              }`}
            >
              {provider.logo_url ? (
                <img 
                  src={provider.logo_url} 
                  alt={provider.logo_alt || `${provider.display_name} logo`}
                  className="w-full h-full object-contain p-1"
                />
              ) : (
                <span className="text-xl">{provider.icon_emoji}</span>
              )}
            </div>
            <span>{getModalTitle()}</span>
          </DialogTitle>
          <DialogDescription>
            {getModalDescription()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Username/Email */}
          <div className="space-y-2">
            <Label htmlFor="conn-username">Username / Email</Label>
            {isEditing && provider.username_masked && (
              <p className="text-xs text-stone-500 mb-1">
                Current: <span className="font-mono">{provider.username_masked}</span>
              </p>
            )}
            <Input
              id="conn-username"
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={isEditing ? 'Enter new username (or leave blank)' : 'your-email@example.com'}
              data-testid="connection-username"
            />
          </div>
          
          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="conn-password">Password</Label>
            {isEditing && provider.has_password && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 mb-1">
                <Lock size={12} />
                <span>Password saved securely</span>
              </div>
            )}
            <div className="relative">
              <Input
                id="conn-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isEditing ? 'Enter new password (or leave blank)' : 'Enter password'}
                data-testid="connection-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          
          {/* Identity Value (Security Answer / Last 4 SSN) - REQUIRED */}
          <div className="space-y-2">
            <Label htmlFor="conn-identity">
              Last 4 of SSN or Security Answer <span className="text-red-500">*</span>
            </Label>
            {isEditing && provider.has_identity_value && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 mb-1">
                <Lock size={12} />
                <span>Verification value saved</span>
              </div>
            )}
            <Input
              id="conn-identity"
              type="text"
              value={identityValue}
              onChange={(e) => setIdentityValue(e.target.value)}
              placeholder="Enter your last 4 SSN digits or security answer"
              data-testid="connection-identity"
              required
            />
            <p className="text-xs text-stone-500">Required for provider verification during import</p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSaving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="save-connection-btn"
          >
            {isSaving ? (
              <RefreshCw size={14} className="mr-1 animate-spin" />
            ) : (
              <Save size={14} className="mr-1" />
            )}
            {isEditing ? 'Update' : 'Save'} Connection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main Connections Vault Component
 */
const ConnectionsVault = () => {
  const { getAuthHeaders, isSupportMode = false } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  
  // Fetch connections
  const fetchConnections = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/connections`, {
        headers: {
          ...getAuthHeaders(),
          ...(isSupportMode ? { 'X-Support-Mode': 'true' } : {})
        }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch connections:', error);
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, isSupportMode]);
  
  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);
  
  // Open connect modal
  const handleConnect = (provider) => {
    setSelectedProvider(provider);
    setIsEditing(false);
    setModalOpen(true);
  };
  
  // Open edit modal
  const handleEdit = (provider) => {
    setSelectedProvider(provider);
    setIsEditing(true);
    setModalOpen(true);
  };
  
  // Save connection
  const handleSaveConnection = async (formData) => {
    if (!selectedProvider) return;
    
    setSaving(true);
    try {
      if (isEditing) {
        // Update existing
        await axios.put(
          `${API}/connections/${selectedProvider.provider_key}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Connection updated');
      } else {
        // Create new
        await axios.post(
          `${API}/connections`,
          {
            provider_key: selectedProvider.provider_key,
            ...formData,
            is_saved_for_reuse: true
          },
          { headers: getAuthHeaders() }
        );
        toast.success('Connection saved');
      }
      
      setModalOpen(false);
      fetchConnections();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save connection');
    } finally {
      setSaving(false);
    }
  };
  
  // Delete confirmation
  const handleDeleteClick = (provider) => {
    setProviderToDelete(provider);
    setDeleteDialogOpen(true);
  };
  
  // Delete connection
  const handleDeleteConfirm = async () => {
    if (!providerToDelete) return;
    
    try {
      await axios.delete(
        `${API}/connections/${providerToDelete.provider_key}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Connection deleted');
      setDeleteDialogOpen(false);
      setProviderToDelete(null);
      fetchConnections();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete connection');
    }
  };
  
  // Quick import using shared refresh availability logic
  const handleQuickImport = async (provider) => {
    try {
      const refreshRes = await axios.get(
        `${API}/credit-report/refresh-status/${provider.provider_key}`,
        { headers: getAuthHeaders() },
      );
      const actionState = buildRefreshActionState(refreshRes.data, { hasCredentials: true });

      await executeProviderRefreshAction({
        provider: provider.provider_key,
        actionState,
        hasCredentials: true,
        onImportStarted: () => {
          toast.success(`Import started for ${provider.provider_name || provider.provider_key}`);
        },
        onNeedCredentials: () => {
          handleEdit(provider);
        },
      });
    } catch (error) {
      console.error('Quick import error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to start import');
    }
  };
  
  if (loading) {
    return (
      <div className="space-y-6" data-testid="connections-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40" />
        <Skeleton className="h-40" />
      </div>
    );
  }
  
  const { monitoring_services = [], bureaus = [], total_connected = 0 } = data || {};
  
  return (
    <div className="space-y-6" data-testid="connections-vault">
      {/* Support Mode Warning */}
      {isSupportMode && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
          <Shield size={20} className="text-amber-600 shrink-0" />
          <div>
            <p className="font-medium text-amber-800">Support Mode Active</p>
            <p className="text-sm text-amber-700">
              Credential management is blocked while viewing this account.
            </p>
          </div>
        </div>
      )}
      
      {/* Summary Stats */}
      <div 
        className="flex items-center gap-4 p-4 rounded-xl"
        style={{ background: colors.mint50 }}
      >
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: colors.mint100 }}
        >
          <PlugZap size={24} style={{ color: colors.brandGreen600 }} />
        </div>
        <div>
          <p className="font-semibold text-stone-900">
            {total_connected} {total_connected === 1 ? 'Connection' : 'Connections'} Saved
          </p>
          <p className="text-sm text-stone-500">
            Securely stored for quick report imports
          </p>
        </div>
      </div>
      
      {/* Credit Monitoring Services */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{GROUP_HEADERS.monitoring_service.icon}</span>
          <div>
            <h3 className="font-semibold text-stone-900">
              {GROUP_HEADERS.monitoring_service.title}
            </h3>
            <p className="text-xs text-stone-500">
              {GROUP_HEADERS.monitoring_service.description}
            </p>
          </div>
        </div>
        
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {monitoring_services.map((provider) => (
            <ConnectionCard
              key={provider.provider_key}
              provider={provider}
              isSupportMode={isSupportMode}
              onConnect={handleConnect}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onQuickImport={handleQuickImport}
              isLoading={saving}
            />
          ))}
        </div>
      </div>
      
      {/* Bureau Logins */}
      <div className="space-y-3 pt-4 border-t border-stone-200">
        <div className="flex items-center gap-2">
          <span className="text-lg">{GROUP_HEADERS.bureau.icon}</span>
          <div>
            <h3 className="font-semibold text-stone-900">
              {GROUP_HEADERS.bureau.title}
            </h3>
            <p className="text-xs text-stone-500">
              {GROUP_HEADERS.bureau.description}
            </p>
          </div>
        </div>
        
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {bureaus.map((provider) => (
            <ConnectionCard
              key={provider.provider_key}
              provider={provider}
              isSupportMode={isSupportMode}
              onConnect={handleConnect}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onQuickImport={handleQuickImport}
              isLoading={saving}
            />
          ))}
        </div>
      </div>
      
      {/* Help Text */}
      <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
        <h4 className="font-medium text-stone-700 mb-2">How it works</h4>
        <ul className="text-sm text-stone-600 space-y-1.5">
          <li className="flex items-start gap-2">
            <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
            <span>Your credentials are encrypted using AES-256 encryption</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
            <span>Passwords are never displayed — only used for server-side imports</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
            <span>Use <strong>Quick Import</strong> to pull your latest report in one click</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
            <span>Support staff cannot view or use your saved credentials</span>
          </li>
        </ul>
      </div>
      
      {/* Connection Modal */}
      <ConnectionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        provider={selectedProvider}
        isEditing={isEditing}
        onSave={handleSaveConnection}
        isSaving={saving}
      />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Connection?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your saved {providerToDelete?.display_name} credentials.
              You'll need to re-enter them for future imports.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConnectionsVault;
